import { CommanServices } from '@/services/commanServices';
import axios from 'axios';


/**
 * Check if a name is duplicate in the specified module
 * @param {string} module - Module name: 'incident-classifications', 'action-cards', 'teams'
 * @param {string} name - Name to check for duplicates
 * @param {string} excludeId - ID to exclude from check (for updates)
 * @returns {Promise<{isDuplicate: boolean, existingName: string, message: string}>}
 */
export const checkDuplicate = async (module, name, excludeId = null) => {
  try {
    if (!name || !name.trim()) {
      return {
        isDuplicate: false,
        existingName: '',
        message: ''
      };
    }

    const params = new URLSearchParams({ name: name.trim() });
    if (excludeId) {
      params.append('exclude_id', excludeId);
    }

    const response = await CommanServices.checkduplicatedata(module, name, params)

    const { is_duplicate, existing_name } = response.data;
    if (response.status) {
      return {
        isDuplicate: is_duplicate,
        existingName: existing_name,
        message: is_duplicate 
          ? `A ${getModuleDisplayName(module)} with this name already exists: "${existing_name}". Please choose a different name.`
          : ''
      };
    }else{
      return {
        isDuplicate: is_duplicate,
        existingName: existing_name,
        message: is_duplicate 
          ? `A ${getModuleDisplayName(module)} with this name already exists: "${existing_name}". Please choose a different name.`
          : ''
      };
    }

  } catch (error) {
    console.error('Error checking duplicate:', error);
    return {
      isDuplicate: false,
      existingName: '',
      message: 'Unable to check for duplicates. Please try again.'
    };
  }
};

/**
 * Get display name for module
 */
const getModuleDisplayName = (module) => {
  const moduleNames = {
    'incident-classifications': 'incident type',
    'action-cards': 'action card',
    'teams': 'team'
  };
  return moduleNames[module] || module;
};

/**
 * Debounce function for reducing api calls during typing
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Hook for duplicate checking with debouncing
 */
import { useState, useCallback, useRef } from 'react';

export const useDuplicateChecker = (module, excludeId = null, debounceMs = 500) => {
  const [duplicateStatus, setDuplicateStatus] = useState({
    isChecking: false,
    isDuplicate: false,
    existingName: '',
    message: ''
  });

  const abortControllerRef = useRef(null);

  const checkDuplicateDebounced = useCallback(
    debounce(async (name) => {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!name || !name.trim()) {
        setDuplicateStatus({
          isChecking: false,
          isDuplicate: false,
          existingName: '',
          message: ''
        });
        return;
      }

      setDuplicateStatus(prev => ({ ...prev, isChecking: true }));

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        const result = await checkDuplicate(module, name, excludeId);
        
        // Only update state if request wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          setDuplicateStatus({
            isChecking: false,
            isDuplicate: result.isDuplicate,
            existingName: result.existingName,
            message: result.message
          });
        }
      } catch (error) {
        if (!abortControllerRef.current.signal.aborted) {
          setDuplicateStatus({
            isChecking: false,
            isDuplicate: false,
            existingName: '',
            message: 'Unable to check for duplicates'
          });
        }
      }
    }, debounceMs),
    [module, excludeId, debounceMs]
  );

  const checkName = useCallback((name) => {
    checkDuplicateDebounced(name);
  }, [checkDuplicateDebounced]);

  const clearStatus = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setDuplicateStatus({
      isChecking: false,
      isDuplicate: false,
      existingName: '',
      message: ''
    });
  }, []);

  return {
    ...duplicateStatus,
    checkName,
    clearStatus
  };
};

export default {
  checkDuplicate,
  debounce,
  useDuplicateChecker
};